import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import {
	Component,
	Output,
	Input,
	EventEmitter,
	ElementRef,
	Directive,
	Provider,
	forwardRef,
	Injector,
	ViewChild,
	EnvironmentInjector,
	Self,
	inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, NgControl } from '@angular/forms';
import { GlobalValidator } from '../Classes/globalValidator';
import { Autosize } from './autosize.directive';

@Component({
	standalone: false,
	selector: 'visiba-input',
	template: `
		<div
			[ngSwitch]="type"
			[ngStyle]="{ 'border-color': wallColor }"
			class="wallInput"
			[class.wallStyling]="type != 'checkbox' && type != 'radio'"
			[class.hasIcon]="icon != null"
		>
			<ng-template ngSwitchCase="radio">
				<div class="radioWrap row" *ngFor="let o of options">
					<input
						[tabindex]="tabInd"
						type="radio"
						[(ngModel)]="localModel"
						[readonly]="readonly"
						[disabled]="readonly"
						[name]="rndIdentifier"
						[value]="o[value]"
						[id]="'radio_' + rndIdentifier + o[value]"
						(change)="validateAndEmitValue()"
					/>
					<label [attr.for]="'radio_' + rndIdentifier + o[value]">
						<visiba-svg a="Radio"></visiba-svg>
						<visiba-svg a="RadioChecked"></visiba-svg>
						<visiba-svg class="icon" *ngIf="icon != null" [a]="icon"></visiba-svg>
						<img *ngIf="labelIcon !== null" [src]="o[labelIcon]" class="labelIcon" />
						<span [innerHTML]="o[label]"></span>
					</label>
				</div>
			</ng-template>
			<ng-template ngSwitchCase="checkbox">
				<div class="checkboxWrap">
					<input
						[tabindex]="tabInd"
						type="checkbox"
						[readonly]="readonly"
						[disabled]="readonly"
						[id]="'check_' + rndIdentifier"
						[(ngModel)]="localModel"
						(ngModelChange)="validateAndEmitValue()"
					/>
					<label [for]="'check_' + rndIdentifier">
						<visiba-svg a="Checkbox"></visiba-svg>
						<visiba-svg a="CheckboxChecked"></visiba-svg>
						<visiba-svg class="icon" *ngIf="icon != null" [a]="icon"></visiba-svg>
						<span [innerHTML]="localPlaceholder"></span>
					</label>
				</div>
			</ng-template>
			<ng-template ngSwitchCase="textarea">
				<textarea
					[tabindex]="tabInd"
					(blur)="onBlurEvent()"
					[(ngModel)]="localModel"
					#autosizeDirective="autosizedirective"
					autosize
					[readonly]="readonly"
					[placeholder]="localPlaceholder"
					(ngModelChange)="validateAndEmitValue()"
				></textarea>
			</ng-template>
			<ng-template ngSwitchDefault>
				<input
					[tabindex]="tabInd"
					(blur)="onBlurEvent()"
					[attr.autofocus]="autofocus ? '' : null"
					[(ngModel)]="localModel"
					[ngStyle]="{ color: wallColor }"
					type="{{ type }}"
					[readonly]="readonly"
					[placeholder]="localPlaceholder"
					(ngModelChange)="validateAndEmitValue()"
					[class.svg]="icon != null && icon != ''"
				/>
			</ng-template>
			<ng-template [ngIf]="type != 'checkbox' && type != 'radio'">
				<visiba-svg *ngIf="icon != null" class="iconSvg" [color]="wallColor" [a]="icon"></visiba-svg>
				<label class="ellipsis" [class.value]="localModel != null && localModel != ''" [class.svg]="icon != null">{{
					localPlaceholder
				}}</label>
			</ng-template>
			<span *ngIf="currentError != null" class="error" [class.value]="localModel != null && localModel != ''">{{ currentError }}</span>
		</div>
	`,
	host: {
		'[class.readonly]': 'readonly',
	},
})
export class VisibaInputComponent implements ControlValueAccessor {
	@Input() tabInd: number = 0;
	@Input() icon: string = null;
	@Input() wallColor: string = null;
	@Input() type: string = null;
	@Input() placeholder: string = null;
	@Input() staticplaceholder: string = null;
	@Input() readonly: boolean = false;
	@Input() autofocus: boolean = false;
	@Input() errorMessages: { [name: string]: string } | string = 'default';

	@Input('modelValue') localModel: any = null;

	// Colorpicker
	@Input() cpPosition: string = null;
	@Input() cpOutputFormat: string = null;

	// Select / Radio
	@Input() label: string = 'label';
	@Input() labelIcon: string = null;
	@Input() value: string = 'value';
	@Input() options: Array<any> = [];

	// Textarea
	@ViewChild('autosizeDirective', { static: false }) autosizeDirective: Autosize = null;

	@Output() onChange = new EventEmitter();
	@Output() onBlur = new EventEmitter();

	currentError: string = null;
	localPlaceholder: string = '';
	rndIdentifier: string = Math.floor((1 + Math.random()) * 0x1000000).toString(16);

	constructor(
		public elem: ElementRef,
		@Self() public controlDir: NgControl,
		private translateService: TranslateService,
	) {
		controlDir.valueAccessor = this;
	}

	/**
	 *
	 */
	writeValue(value: any) {
		this.localModel = value;

		if (this.type !== 'radio' && !isNaN(parseFloat(value)) && isFinite(value)) {
			this.localModel = value.toString();
		}

		window.setTimeout(() => {
			this.currentError = GlobalValidator.validateFormControl(
				this.translateService,
				this.controlDir.control as UntypedFormControl,
				this.errorMessages,
			);
			if (typeof this.autosizeDirective !== 'undefined' && this.autosizeDirective !== null) {
				this.autosizeDirective.adjust();
			}
		});
	}

	//Call to push value
	propagateChange = (_: any) => {};

	registerOnChange(fn) {
		this.propagateChange = fn;
	}

	registerOnTouch() {}
	registerOnTouched() {}

	/**
	 *
	 */

	ngOnInit() {
		if (this.placeholder !== null) {
			this.localPlaceholder = this.translateService.get(this.placeholder) + (this.elem.nativeElement.required ? ' *' : '');
		} else if (this.staticplaceholder != null) this.localPlaceholder = this.staticplaceholder;
	}

	validateAndEmitValue() {
		if (this.localModel === '') this.localModel = null;

		this.propagateChange(this.localModel);
		this.currentError = GlobalValidator.validateFormControl(
			this.translateService,
			this.controlDir.control as UntypedFormControl,
			this.errorMessages,
		);
		if (typeof this.onChange !== 'undefined') {
			window.setTimeout(() => {
				this.onChange.emit(this.localModel);
			}, 10);
		}
	}

	onBlurEvent() {
		if (typeof this.onBlur !== 'undefined') this.onBlur.emit(this.localModel);
	}
}
